import React, { useState, useRef, useEffect } from 'react';
import Layout from '../../components/layout/Layout';
import api from '../../services/api';
import { Send, RotateCcw, Bot, User, Loader, Copy, MessageCircle, Check, FileText, FileType } from 'lucide-react';

const SUGGESTIONS = [
  'Give me today\'s monitoring briefing',
  'Who is in the field right now?',
  'Show pending leave requests',
  'Which contracts are expiring soon?',
  'Show facility scores from recent PMR reports',
  'Compare last month vs this month performance',
  'Which districts have no Assistant Director assigned?',
  'Show me the full officer roster',
];

function getGreeting() {
  const h = new Date().getHours();
  if (h < 12) return 'morning';
  if (h < 17) return 'afternoon';
  return 'evening';
}

// Convert markdown to WhatsApp-formatted text
const toWhatsApp = (text) => {
  return text
    // Convert markdown tables to plain list
    .replace(/\|(.+)\|/g, (match, content) => {
      const cells = content.split('|').map(c => c.trim()).filter(Boolean);
      return cells.join(' • ');
    })
    // Remove table separator rows (---|---|---)
    .replace(/^[\s|:-]+$/gm, '')
    // Bold: **text** → *text* (WhatsApp bold)
    .replace(/\*\*(.*?)\*\*/g, '*$1*')
    // Remove italic markdown
    .replace(/\*(.*?)\*/g, '$1')
    // Keep bullet points
    .replace(/^- /gm, '• ')
    // Clean up multiple blank lines
    .replace(/\n{3,}/g, '\n\n')
    .trim();
};

const MonitoringAgent = () => {
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: `Good ${getGreeting()}! I am your *FMAS Monitoring Agent*.\n\nI have real-time access to all field monitoring data across your districts. You can ask me about:\n\n• Visit compliance and performance\n• Officer attendance and rankings\n• Pending or missing PMR reports\n• Overdue institute visits\n• District comparisons\n• Training program overview\n\nHow can I assist you today?`,
      timestamp: new Date().toISOString(),
    }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [copiedIndex, setCopiedIndex] = useState(null);
  const [exportingKey, setExportingKey] = useState(null);
  const bottomRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = async (text) => {
    const userText = (text || input).trim();
    if (!userText || loading) return;

    setInput('');
    setMessages(prev => [...prev, {
      role: 'user',
      content: userText,
      timestamp: new Date().toISOString(),
    }]);
    setLoading(true);

    try {
      const res = await api.post('/agents/monitoring/chat', { message: userText });
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: res.data.reply,
        timestamp: res.data.timestamp,
      }]);
    } catch (err) {
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: '⚠️ Sorry, I encountered an error. Please try again.',
        timestamp: new Date().toISOString(),
        error: true,
      }]);
    } finally {
      setLoading(false);
    }
  };

  const resetChat = async () => {
    try {
      await api.post('/agents/monitoring/reset');
    } catch (_) {}
    setMessages([{
      role: 'assistant',
      content: `Conversation reset. How can I assist you?`,
      timestamp: new Date().toISOString(),
    }]);
  };

  const copyText = (text, index) => {
    navigator.clipboard.writeText(text);
    setCopiedIndex(`copy-${index}`);
    setTimeout(() => setCopiedIndex(null), 2000);
  };

  const copyAsWhatsApp = (text, index) => {
    const whatsappText = toWhatsApp(text);
    navigator.clipboard.writeText(whatsappText);
    setCopiedIndex(`wa-${index}`);
    setTimeout(() => setCopiedIndex(null), 2000);
  };

  const deriveTitle = (text) => {
    const firstLine = text.split('\n').find(l => l.trim());
    if (!firstLine) return 'FMAS Report';
    return firstLine.replace(/^#+\s*/, '').replace(/\*\*/g, '').trim().slice(0, 80) || 'FMAS Report';
  };

  const exportReport = async (text, index, format) => {
    const key = `${format}-${index}`;
    setExportingKey(key);
    try {
      const title = deriveTitle(text);
      const res = await api.post(
        `/agents/monitoring/export/${format}`,
        { content: text, title },
        { responseType: 'blob' }
      );
      const blob = new Blob([res.data], {
        type: format === 'pdf'
          ? 'application/pdf'
          : 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${title.replace(/[^a-z0-9]+/gi, '_').slice(0, 60)}.${format === 'pdf' ? 'pdf' : 'pptx'}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert('Failed to generate report. Please try again.');
    } finally {
      setExportingKey(null);
    }
  };

  const formatMessage = (text) => {
    return text
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.*?)\*/g, '<em>$1</em>')
      .replace(/^• /gm, '&bull; ')
      // Render markdown tables as HTML tables
      .replace(/(\|.+\|\n?)+/g, (tableBlock) => {
        const rows = tableBlock.trim().split('\n').filter(r => r.trim() && !/^\|[-:| ]+\|$/.test(r.trim()));
        if (rows.length < 1) return tableBlock;
        const parseRow = (row) => row.split('|').filter((_, i, a) => i > 0 && i < a.length - 1).map(c => c.trim());
        const [headerRow, ...bodyRows] = rows;
        const headers = parseRow(headerRow);
        const headerHtml = `<thead><tr>${headers.map(h => `<th>${h}</th>`).join('')}</tr></thead>`;
        const bodyHtml = `<tbody>${bodyRows.map(r => `<tr>${parseRow(r).map(c => `<td>${c}</td>`).join('')}</tr>`).join('')}</tbody>`;
        return `<div class="overflow-x-auto my-2"><table class="wa-table">${headerHtml}${bodyHtml}</table></div>`;
      })
      .replace(/\n/g, '<br/>');
  };

  return (
    <Layout title="Monitoring Agent">
      <style>{`
        .wa-table { border-collapse: collapse; width: 100%; font-size: 12px; }
        .wa-table th { background: #1e3a5f; color: white; padding: 6px 10px; text-align: left; }
        .wa-table td { padding: 5px 10px; border-bottom: 1px solid #e5e7eb; }
        .wa-table tr:nth-child(even) td { background: #f9fafb; }
      `}</style>

      <div className="flex flex-col h-[calc(100vh-120px)] max-w-4xl mx-auto">

        {/* Header */}
        <div className="bg-blue-900 text-white rounded-xl p-4 mb-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-700 rounded-full flex items-center justify-center">
              <Bot size={22} />
            </div>
            <div>
              <h2 className="font-bold text-lg">FMAS Monitoring Agent</h2>
              <p className="text-blue-300 text-xs">Powered by Claude AI · Real-time data access</p>
            </div>
          </div>
          <button
            onClick={resetChat}
            className="flex items-center gap-2 bg-blue-800 hover:bg-blue-700 px-3 py-1.5 rounded-lg text-sm"
          >
            <RotateCcw size={14} />
            New Chat
          </button>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto bg-white rounded-xl shadow p-4 mb-4 space-y-4">
          {messages.map((msg, i) => (
            <div key={i} className={`flex gap-3 ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}>
              {/* Avatar */}
              <div className={`w-8 h-8 rounded-full flex-shrink-0 flex items-center justify-center text-white text-sm
                ${msg.role === 'assistant' ? 'bg-blue-800' : 'bg-green-700'}`}>
                {msg.role === 'assistant' ? <Bot size={16} /> : <User size={16} />}
              </div>

              {/* Bubble */}
              <div className={`max-w-[80%] rounded-2xl px-4 py-3 text-sm leading-relaxed
                ${msg.role === 'assistant'
                  ? msg.error ? 'bg-red-50 text-red-700 border border-red-200' : 'bg-gray-50 text-gray-800 border border-gray-200'
                  : 'bg-blue-800 text-white'}`}>
                <div dangerouslySetInnerHTML={{ __html: formatMessage(msg.content) }} />

                <div className={`flex items-center gap-2 mt-2 ${msg.role === 'user' ? 'justify-start flex-row-reverse' : 'justify-between'}`}>
                  <p className={`text-xs ${msg.role === 'user' ? 'text-blue-300' : 'text-gray-400'}`}>
                    {new Date(msg.timestamp).toLocaleTimeString('en-PK', { hour: '2-digit', minute: '2-digit', hour12: true })}
                  </p>

                  {/* Action buttons — only on assistant messages */}
                  {msg.role === 'assistant' && !msg.error && (
                    <div className="flex items-center gap-1">
                      {/* Copy plain text */}
                      <button
                        onClick={() => copyText(msg.content, i)}
                        title="Copy text"
                        className="flex items-center gap-1 text-xs text-gray-400 hover:text-gray-600 px-2 py-1 rounded-lg hover:bg-gray-200 transition"
                      >
                        {copiedIndex === `copy-${i}` ? <Check size={12} className="text-green-500" /> : <Copy size={12} />}
                        {copiedIndex === `copy-${i}` ? 'Copied!' : 'Copy'}
                      </button>

                      {/* Copy as WhatsApp */}
                      <button
                        onClick={() => copyAsWhatsApp(msg.content, i)}
                        title="Copy as WhatsApp message"
                        className="flex items-center gap-1 text-xs text-gray-400 hover:text-green-600 px-2 py-1 rounded-lg hover:bg-green-50 transition"
                      >
                        {copiedIndex === `wa-${i}`
                          ? <><Check size={12} className="text-green-500" /> <span className="text-green-600">Ready!</span></>
                          : <><MessageCircle size={12} /> WhatsApp</>
                        }
                      </button>

                      {/* Export PDF */}
                      <button
                        onClick={() => exportReport(msg.content, i, 'pdf')}
                        disabled={exportingKey === `pdf-${i}`}
                        title="Download as PDF"
                        className="flex items-center gap-1 text-xs text-gray-400 hover:text-red-600 px-2 py-1 rounded-lg hover:bg-red-50 transition disabled:opacity-50"
                      >
                        {exportingKey === `pdf-${i}`
                          ? <Loader size={12} className="animate-spin" />
                          : <FileText size={12} />}
                        PDF
                      </button>

                      {/* Export PPTX */}
                      <button
                        onClick={() => exportReport(msg.content, i, 'pptx')}
                        disabled={exportingKey === `pptx-${i}`}
                        title="Download as PowerPoint"
                        className="flex items-center gap-1 text-xs text-gray-400 hover:text-orange-600 px-2 py-1 rounded-lg hover:bg-orange-50 transition disabled:opacity-50"
                      >
                        {exportingKey === `pptx-${i}`
                          ? <Loader size={12} className="animate-spin" />
                          : <FileType size={12} />}
                        PPT
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}

          {/* Loading indicator */}
          {loading && (
            <div className="flex gap-3">
              <div className="w-8 h-8 rounded-full bg-blue-800 flex items-center justify-center text-white">
                <Bot size={16} />
              </div>
              <div className="bg-gray-50 border border-gray-200 rounded-2xl px-4 py-3 flex items-center gap-2">
                <Loader size={16} className="animate-spin text-blue-600" />
                <span className="text-sm text-gray-500">Analyzing data...</span>
              </div>
            </div>
          )}
          <div ref={bottomRef} />
        </div>

        {/* Suggestions */}
        {messages.length <= 1 && (
          <div className="mb-3 flex flex-wrap gap-2">
            {SUGGESTIONS.map((s, i) => (
              <button
                key={i}
                onClick={() => sendMessage(s)}
                className="text-xs bg-white border border-gray-300 hover:border-blue-400 hover:bg-blue-50 text-gray-600 hover:text-blue-700 px-3 py-1.5 rounded-full transition"
              >
                {s}
              </button>
            ))}
          </div>
        )}

        {/* Input */}
        <div className="flex gap-3">
          <input
            type="text"
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && !e.shiftKey && sendMessage()}
            placeholder="Ask about visits, officers, reports, districts..."
            className="flex-1 border border-gray-300 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            disabled={loading}
          />
          <button
            onClick={() => sendMessage()}
            disabled={loading || !input.trim()}
            className="bg-blue-800 hover:bg-blue-700 disabled:bg-gray-300 text-white px-5 py-3 rounded-xl flex items-center gap-2 transition"
          >
            <Send size={18} />
          </button>
        </div>

      </div>
    </Layout>
  );
};

export default MonitoringAgent;
